import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { getAllowedPeriodInDays, isClosedDay } from '../utils/bookingWindow';

const CustomCalendar = ({ selectedDate, onDateSelect, onClose, allowedPeriodInDays }) => {
    const company = useSelector((state) => state.companyReducer.company);
    const companyTimezone = company?.operatingLocation?.zoneId || 'America/New_York';
    const resolvedAllowedPeriodInDays = allowedPeriodInDays || getAllowedPeriodInDays(company);
    const todayInCompanyTimezone = useMemo(
        () => moment().tz(companyTimezone).startOf('day'),
        [companyTimezone]
    );
    const maxSelectableDate = useMemo(
        () => todayInCompanyTimezone.clone().add(resolvedAllowedPeriodInDays, 'days'),
        [todayInCompanyTimezone, resolvedAllowedPeriodInDays]
    );

    const getSelectedMoment = useMemo(() => {
        if (!selectedDate) {
            return todayInCompanyTimezone.clone();
        }
        const selectedMoment = moment.tz(selectedDate, 'YYYY-MM-DD', companyTimezone).startOf('day');
        return selectedMoment.isAfter(maxSelectableDate, 'day')
            ? todayInCompanyTimezone.clone()
            : selectedMoment;
    }, [selectedDate, companyTimezone, todayInCompanyTimezone, maxSelectableDate]);

    const [currentMonth, setCurrentMonth] = useState(() => {
        return getSelectedMoment.clone().startOf('month').toDate();
    });

    const calendarRef = useRef(null);

    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const getMonthYearDisplay = (date) => {
        const month = date.getMonth() + 1;
        const year  = date.getFullYear();
        return `${month}/${year}`;
    };

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Close on Escape
    useEffect(() => {
        const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
        const minVisibleMonth = todayInCompanyTimezone.clone().startOf('month');
        const maxVisibleMonth = maxSelectableDate.clone().startOf('month');
        const visibleMonth = moment(currentMonth).tz(companyTimezone).startOf('month');

        if (visibleMonth.isBefore(minVisibleMonth, 'month')) {
            setCurrentMonth(minVisibleMonth.toDate());
            return;
        }

        if (visibleMonth.isAfter(maxVisibleMonth, 'month')) {
            setCurrentMonth(maxVisibleMonth.toDate());
        }
    }, [currentMonth, companyTimezone, todayInCompanyTimezone, maxSelectableDate]);

    const getMonthInfo = useCallback((date) => {
        const monthMoment      = moment(date).tz(companyTimezone);
        const year             = monthMoment.year();
        const month            = monthMoment.month();
        const firstDay         = moment.tz([year, month, 1], companyTimezone);
        const lastDay          = firstDay.clone().endOf('month');
        const daysInMonth      = lastDay.date();
        const startingDayOfWeek = firstDay.day();
        return { daysInMonth, startingDayOfWeek, year, month, firstDay };
    }, [companyTimezone]);

    const isDateDisabled = useCallback((dateMoment) => {
        if (dateMoment.isBefore(todayInCompanyTimezone, 'day')) return true;
        if (dateMoment.isAfter(maxSelectableDate, 'day')) return true;
        if (isClosedDay(dateMoment, company, companyTimezone)) return true;
        return false;
    }, [maxSelectableDate, todayInCompanyTimezone, company, companyTimezone]);

    const calendarDays = useMemo(() => {
        const { daysInMonth, startingDayOfWeek, year, month } = getMonthInfo(currentMonth);
        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) days.push(null);

        const today          = todayInCompanyTimezone;
        const selectedMoment = getSelectedMoment;

        for (let day = 1; day <= daysInMonth; day++) {
            const dateMoment = moment.tz([year, month, day], companyTimezone);
            const dateString = dateMoment.format('YYYY-MM-DD');
            const isToday    = dateMoment.isSame(today, 'day');
            const isSelected = selectedMoment.isSame(dateMoment, 'day');
            const disabled   = isDateDisabled(dateMoment);
            days.push({ dateMoment, day, disabled, isToday, isSelected, dateString });
        }

        return days;
    }, [currentMonth, companyTimezone, todayInCompanyTimezone, getSelectedMoment, getMonthInfo, isDateDisabled]);

    const minVisibleMonth = todayInCompanyTimezone.clone().startOf('month');
    const maxVisibleMonth = maxSelectableDate.clone().startOf('month');
    const currentMonthMoment = moment(currentMonth).tz(companyTimezone).startOf('month');
    const isPrevMonthDisabled = currentMonthMoment.isSameOrBefore(minVisibleMonth, 'month');
    const isNextMonthDisabled = currentMonthMoment.isSameOrAfter(maxVisibleMonth, 'month');

    const handlePrevMonth = () => {
        if (isPrevMonthDisabled) {
            return;
        }
        setCurrentMonth(prev =>
            moment(prev).tz(companyTimezone).subtract(1, 'month').toDate()
        );
    };

    const handleNextMonth = () => {
        if (isNextMonthDisabled) {
            return;
        }
        setCurrentMonth(prev =>
            moment(prev).tz(companyTimezone).add(1, 'month').toDate()
        );
    };

    const handleDateClick = (dateObj) => {
        if (!dateObj.disabled && dateObj.dateString) {
            onDateSelect(dateObj.dateString);
            onClose();
        }
    };

    const goToToday = () => {
        const today = todayInCompanyTimezone.clone();
        setCurrentMonth(today.clone().startOf('month').toDate());
        if (!isDateDisabled(today)) {
            onDateSelect(today.format('YYYY-MM-DD'));
        }
    };

    return (
        <motion.div
            ref={calendarRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-[9999] bg-white shadow-2xl border border-slate-200 flex flex-col"
            style={{
                borderRadius: '12px',
                /* Fluid width: 288px on small screens, 320px on md+ */
                width: 'min(320px, calc(100vw - 32px))',
                /* Cap height and let the body scroll instead of clipping off-screen
                   on short viewports (landscape phones, small tablets) */
                maxHeight: 'calc(100vh - 32px)'
            }}
        >
            {/* ── Header ── */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between text-white rounded-t-xl shrink-0">
                <div className="flex items-center gap-1 sm:gap-2">
                    <motion.button
                        whileHover={!isPrevMonthDisabled ? { scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' } : {}}
                        whileTap={!isPrevMonthDisabled ? { scale: 0.9 } : {}}
                        onClick={handlePrevMonth}
                        disabled={isPrevMonthDisabled}
                        className={`p-2.5 rounded-lg transition-all min-w-[36px] min-h-[36px] flex items-center justify-center ${isPrevMonthDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </motion.button>

                    <motion.div
                        className="font-semibold text-sm min-w-[70px] sm:min-w-[80px] text-center"
                        key={`${currentMonth.getMonth()}-${currentMonth.getFullYear()}`}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {getMonthYearDisplay(currentMonth)}
                    </motion.div>

                    <motion.button
                        whileHover={!isNextMonthDisabled ? { scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' } : {}}
                        whileTap={!isNextMonthDisabled ? { scale: 0.9 } : {}}
                        onClick={handleNextMonth}
                        disabled={isNextMonthDisabled}
                        className={`p-2.5 rounded-lg transition-all min-w-[36px] min-h-[36px] flex items-center justify-center ${isNextMonthDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </motion.button>
                </div>

                <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2.5 rounded-lg transition-all min-w-[36px] min-h-[36px] flex items-center justify-center"
                >
                    <X className="w-4 h-4" />
                </motion.button>
            </div>

            <div className="overflow-y-auto">
            {/* ── Days of week ── */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 px-3 sm:px-4 pt-3 pb-2 bg-slate-50">
                {daysOfWeek.map(day => (
                    <div key={day} className="text-center text-[10px] sm:text-xs font-semibold text-slate-600">
                        {/* Show 1 letter on very small screens, 3 letters on sm+ */}
                        <span className="inline sm:hidden">{day[0]}</span>
                        <span className="hidden sm:inline">{day}</span>
                    </div>
                ))}
            </div>

            {/* ── Calendar days ── */}
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5 p-3 sm:p-4 bg-white">
                {calendarDays.map((dateObj, index) => (
                    <div key={index} className="aspect-square">
                        {dateObj ? (
                            <motion.button
                                whileHover={!dateObj.disabled ? { scale: 1.08 } : {}}
                                whileTap={!dateObj.disabled ? { scale: 0.92 } : {}}
                                onClick={() => handleDateClick(dateObj)}
                                disabled={dateObj.disabled}
                                className={`w-full h-full flex items-center justify-center text-[11px] sm:text-xs font-medium transition-all rounded-lg
                                    ${dateObj.disabled
                                        ? 'text-slate-300 cursor-not-allowed bg-slate-50'
                                        : dateObj.isSelected
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : dateObj.isToday
                                                ? 'bg-blue-50 text-blue-700 border border-blue-400'
                                                : 'text-slate-700 hover:bg-blue-50 hover:text-blue-600'
                                    }`}
                                title={dateObj.dateString}
                            >
                                {dateObj.day}
                            </motion.button>
                        ) : (
                            <div className="w-full h-full" />
                        )}
                    </div>
                ))}
            </div>

            {/* ── Footer ── */}
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-50 border-t border-slate-200 rounded-b-xl">
                <div className="flex items-center justify-between mb-2.5 sm:mb-3">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={goToToday}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2.5 sm:px-3 py-1.5 hover:bg-blue-50 rounded transition-colors"
                    >
                        Today
                    </motion.button>
                    <div className="text-xs text-slate-600">
                        {todayInCompanyTimezone.format('M/D/YYYY')}
                    </div>
                </div>

                <div className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-[11px] text-blue-700">
                    Bookings can be made up to {resolvedAllowedPeriodInDays} days in advance through {maxSelectableDate.format('M/D/YYYY')}.
                </div>

                {/* Legend */}
                <div className="grid grid-cols-3 gap-1">
                    <div className="flex flex-col items-center">
                        <div className="w-8 h-5 sm:h-6 flex items-center justify-center mb-1">
                            <div className="w-3 h-3 bg-blue-600 rounded shadow-sm" />
                        </div>
                        <span className="text-[10px] text-slate-600">Selected</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="w-8 h-5 sm:h-6 flex items-center justify-center mb-1">
                            <div className="w-3 h-3 border border-blue-400 rounded bg-blue-50" />
                        </div>
                        <span className="text-[10px] text-slate-600">Today</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="w-8 h-5 sm:h-6 flex items-center justify-center mb-1">
                            <div className="w-3 h-3 bg-slate-100 rounded" />
                        </div>
                        <span className="text-[10px] text-slate-600">Unavailable</span>
                    </div>
                </div>
            </div>
            </div>
        </motion.div>
    );
};

export default CustomCalendar;
